<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	import { Popover } from 'flowbite-svelte';
	// Components
	import Label from '@/Components/Label.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Partials
	import AddNote from '@/Partials/Sport/Event/_AddNote.svelte';
	import Status from '@/Partials/Sport/Event/_Status.svelte';
	import Suspend from '@/Partials/Sport/Event/_Suspend.svelte';
	import MapSportcastEvent from '@/Partials/Sport/Event/_MapSportcastEvent.svelte';
	import OverrideStartTime from '@/Partials/Sport/Event/_OverrideStartTime.svelte';
	// Stores
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	import SportSelectionStore from '@/Stores/Sport/SportSelectionStore.js';
	import SportcastFixtureStore from '@/Stores/Sport/SportcastFixtureStore.js';
	// Constants
	import config from '@/config.js';
	import { RollingType, Status as MarketStatus } from '@/Constants/Market.js';
	import { EventStatus } from '@/Constants/Event';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { convertToSlug } from '@/Services/Helpers';

	dayjs.extend(localizedFormat);

	export let event;
	export let canRefund = true;

	let showAddNote = false;
	let showOverrideStartTime = false;
	let confirmRefundEvent = false;
	let refundingEvent = false;

	$: participants = event && event.participants;
	$: participant1 = (participants && participants.firstWhere('is_home', true)) ?? '';
	$: participant2 = (participants && participants.firstWhere('is_home', false)) ?? '';
	$: keyMarkets = event && $MarketGroupStore && getKeyMarkets();
	$: draw =
		keyMarkets &&
		$SportSelectionStore
			.whereIn(
				'market_id',
				keyMarkets.whereNotIn('status.id', [MarketStatus.Removed, MarketStatus.Pending]).pluck('id').toArray()
			)
			.where('name', 'Draw')
			.first();
	$: filteredKeyMarkets = keyMarkets && getFilteredKeyMarkets(keyMarkets);

	function getFilteredKeyMarkets(providedKeyMarkets) {
		return providedKeyMarkets.take(3).all();
	}

	function getKeyMarkets() {
		let marketIds = $MarketGroupStore
			.where('sportevent_id', event.id)
			.where('is_key', true)
			.pluck('market_ids')
			.first();

		const StatusSortOrder = {
			Open: MarketStatus.Open,
			Suspended: MarketStatus.Suspended,
			Closed: MarketStatus.Closed,
			Pending: MarketStatus.Pending,
			Resulted: MarketStatus.Resulted
		};
		const statusSortOrder = Object.values(StatusSortOrder);

		return (
			marketIds &&
			$MarketStore
				.whereIn('id', marketIds)
				.whereNotIn('status.id', [MarketStatus.Removed])
				.sort(
					// Sort by status first THEN ID,
					(a, b) =>
						statusSortOrder.indexOf((a.override_status && a.override_status.id) ?? a.status.id) -
							statusSortOrder.indexOf((b.override_status && b.override_status.id) ?? b.status.id) || a.id - b.id
				)
				.unique((market) => market.type.id) // Will take the first of each market type found
				.sortBy((market) => marketIds.indexOf(market.id)) // Sort in market group order
		);
	}

	function getKeyMarketPrice(market, participant, row = 1) {
		if (!participant) return '-';
		let price, selection;
		selection = $SportSelectionStore.where('market_id', market.id).where('participant_id', participant.id).first();

		if (!selection) {
			// no participant id match
			// TODO: is the use of "row" reliable?
			selection = $SportSelectionStore.where('market_id', market.id).get(row - 1);
		}

		if (selection) {
			if (selection.name === participant.name) {
				price = `${transformLine(selection, market)} ${selection.display_price}`;
			} else if (selection.name.includes(participant.name)) {
				price =
					selection &&
					'(' + selection.name.replace(participant.name, '').toString().trim() + `) @ ${selection.display_price}`;
			} else {
				price =
					selection && `${selection.name} ${transformLine(selection, market, false)} @ ${selection.display_price}`;
			}
		}

		return (price || '-').trim();
	}

	function transformLine(selection, market, includeAt = true) {
		if (market.rolling_type) {
			if (market.rolling_type.id === RollingType.UnderOver && market.line) {
				return `${selection.line}`;
			} else if (market.rolling_type.id === RollingType.Handicap && selection.line) {
				return '(' + selection.display_line + ')' + (includeAt ? ' @' : '');
			}
		}
		return '';
	}

	function refundEvent() {
		if (!canRefund) {
			return;
		}
		refundingEvent = true;
		confirmRefundEvent = false;
		event
			.cancelEvent()
			.then(() => {
				notify({
					type: 'success',
					title: 'Sport event refunded',
					message: 'Sport event has been successfully refunded',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem refunding the event',
					timeout: 5
				});
			})
			.finally(() => (refundingEvent = false));
	}

	const handle_keydown = (e) => {
		if (e.key === 'Escape' && confirmRefundEvent === true) {
			confirmRefundEvent = false;
		}
	};
</script>

<svelte:window on:keydown={handle_keydown} />

{#if showAddNote}
	<AddNote {event} bind:open={showAddNote} />
{/if}

{#if showOverrideStartTime}
	<OverrideStartTime {event} bind:open={showOverrideStartTime} />
{/if}
<div class="flex flex-col">
	<div class="flex text-sm text-left">
		<div class="w-full">
			<div class="float-left mt-2 mb-1 ml-2">
				<div class="flex flex-row items-center space-x-1 align-middle">
					{dayjs(event.start_time).format('D MMM YYYY, HH:mm')}
					<button
						on:click|preventDefault={() => (showOverrideStartTime = true)}
						class="pl-1 text-xs text-gray-500 hover:text-red-500 focus:outline-none"
						title="Override Start Time"
					>
						<Icon name="time" classes="w-3.5 h-3.5" />
					</button>
					<button
						on:click|preventDefault|stopPropagation={() => (showAddNote = true)}
						id={`event_notes_${event.id}`}
						class="text-xs hover:text-red-500 focus:outline-none"
						class:text-gray-500={!event.note}
						class:text-red-500={event.note}
					>
						<Icon name="edit-square" classes="w-3.5 h-3.5" />
					</button>
					<Popover
						triggeredBy={`#event_notes_${event.id}`}
						class="w-72 bg-white dark:bg-gray-800 dark:border-gray-600"
						placement="bottom-start"
					>
						<div class="p-3 space-y-2" style="white-space: pre-line">
							<h2 class="pt-1 text-lg font-bold text-center">Event Notes</h2>
							<div class="flex flex-col w-full px-3 pt-2">
								{#if event.note}
									<div class="font-medium dark:text-gray-400">
										{event.note}
									</div>
								{:else}
									<div class="font-light dark:text-gray-400">No Event Notes</div>
								{/if}
							</div>
						</div>
					</Popover>

					{#if !!event.competition.sportcast_competition_id && $SportcastFixtureStore
							.where('compId', event.competition.id)
							.count()}
						<MapSportcastEvent {event} />
					{:else if !!event.sportcast_fixture_id}
						<div class="p-1 px-1 bg-blue-200 rounded text-tiny">SGM Mapped</div>
					{/if}
				</div>
			</div>
			{#if canRefund}
				<div class="float-right mb-1 ml-6">
					<LoadingButton
						on:click={() => (confirmRefundEvent = true)}
						title="Refund event"
						className={'p-1 text-white rounded-md focus:outline-none ' +
							(event.status.id == EventStatus.Abandoned ? 'bg-gray-300' : 'bg-orange-500')}
						bind:loading={refundingEvent}
						disabled={event.status.id == EventStatus.Abandoned}
					>
						Refund event
					</LoadingButton>
					{#if confirmRefundEvent}
						<div class="absolute z-40 pt-2">
							<div class="relative py-1 transform -translate-x-20 bg-white border border-gray-200 rounded-md shadow-xl">
								<div
									class="absolute top-0 w-4 h-4 origin-center transform rotate-45 -translate-y-2 bg-white border-t border-l border-gray-400 rounded-sm pointer-events-none translate-x-30"
								/>
								<div class="relative flex p-4 -mr-4 whitespace-nowrap">
									<div class="mr-4 text-xl text-center">
										<p class="px-2 py-4 mb-2 font-semibold">
											ARE YOU SURE YOU WANT<br />TO REFUND THIS EVENT?
										</p>
										<button on:click={() => (confirmRefundEvent = false)} class="focus:outline-none">
											<Label label="No" classes="text-xl px-6 py-2" />
										</button>
										<button on:click={() => refundEvent()} class="ml-10 focus:outline-none">
											<Label label="Yes" classes="text-xl px-6 py-2" />
										</button>
									</div>
								</div>
							</div>
						</div>
						<div
							class="fixed inset-0 z-20 bg-black opacity-50 cursor-default"
							on:click={() => (confirmRefundEvent = false)}
						/>
					{/if}
				</div>
			{/if}
		</div>
	</div>
	<div class="grid items-center text-center event-grid">
		<!-- HEADINGS -->
		<div class="col-start-1 px-2 py-1 font-semibold bg-gray-200">Status</div>
		<div class="px-2 py-1 font-semibold text-left truncate bg-gray-200">
			<a
				href="{config.SPORT_EVENT_URL}{convertToSlug(event.competition.name)}/{convertToSlug(event.name)}/{event.id}"
				title="View on platform"
				rel="noreferrer"
				target="_blank"
				class="hover:text-green-500"
				on:click|stopPropagation
			>
				<span class="flex flex-row items-center justify-between">
					{event.name}
					<Icon name="open" classes="w-4 h-4" />
				</span>
			</a>
		</div>
		{#if keyMarkets}
			{#each filteredKeyMarkets as market}
				<div class="px-2 py-1 font-semibold whitespace-nowrap bg-gray-200">
					{market.type.name}
				</div>
			{/each}
			<!-- ROW 1 -->
			<div class="col-start-1 px-2 py-1">
				<Status {event} />
			</div>
			<div class="px-2 py-1 text-left text-nowrap">
				{(participant1 && participant1.name) || '-'}
			</div>
			{#each filteredKeyMarkets as market}
				<div class="px-2 py-1 whitespace-nowrap">
					{$SportSelectionStore && getKeyMarketPrice(market, participant1, 1)}
				</div>
			{/each}
			<!-- DRAW (if it exists) -->
			{#if draw}
				<div class="col-start-2 px-2 py-1 text-left">{draw.name}</div>
				{#each filteredKeyMarkets as market}
					<div class="px-2 py-1 whitespace-nowrap">
						{#if market.id == draw.market_id}
							<!-- This is to ensure the correct position with the market name -->
							{draw.display_price}
						{/if}
					</div>
				{/each}
			{/if}
			<!-- ROW 2 -->
			<div class="col-start-1 px-2 py-1">
				<Suspend {event} />
			</div>
			<div class="px-2 py-1 text-left">
				{(participant2 && participant2.name) || '-'}
			</div>
			{#each filteredKeyMarkets as market}
				<div class="px-2 py-1 whitespace-nowrap">
					{$SportSelectionStore && getKeyMarketPrice(market, participant2, 2)}
				</div>
			{/each}
		{:else}
			<!-- ROW 1 -->
			<div class="col-start-1 px-2 py-1">
				<Status {event} />
			</div>
			<div class="px-2 py-1 text-left">{participant1 && participant1.name}</div>
			<!-- ROW 2 -->
			<div class="col-start-1 px-2 py-1">
				<Suspend {event} />
			</div>
			<div class="px-2 py-1 text-left">{participant2 && participant2.name}</div>
		{/if}
	</div>
</div>

<style>
	.event-grid {
		grid-template-columns: min-content 1fr repeat(3, min-content);
	}
</style>
